import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { StoreDateTime } from '../../../components/StoreDateTime';
import { useAuthContext } from '../../../context/AuthContext';

interface CandidateDetailHeaderProps {
  title?: string;
  storeName: string | null;
  upc: string | null;
  imageId: string | null;
  onBack: () => void;
  page?: string | null;
  isLoadingNavigation?: boolean;
  showNavigation?: boolean;
  onPreviousImage?: () => void;
  onNextImage?: () => void;
  showProductSelection?: boolean;
  onRejectSelection: () => void;
  onAcceptSelection: () => void;
  detectedAt?: string | null;
  storeTimeZone?: string;
  disableNextImage?: boolean;
  navigationExtra?: React.ReactNode;
  extraActions?: React.ReactNode;
}

export const CandidateDetailHeader: React.FC<CandidateDetailHeaderProps> = ({
  title = 'Candidate Detail',
  storeName,
  upc,
  imageId,
  onBack,
  page,
  isLoadingNavigation,
  showNavigation,
  onPreviousImage,
  onNextImage,
  showProductSelection,
  onRejectSelection,
  onAcceptSelection,
  detectedAt,
  storeTimeZone,
  disableNextImage,
  navigationExtra,
  extraActions,
}) => {
  const { hasInternalAccess } = useAuthContext();

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-600 mt-1">
            {storeName && `Store: ${storeName}`}
            {storeName && upc ? ' • ' : ''}
            {upc && `UPC: ${upc}`}
            {imageId && ` • Image ID: ${imageId}`}
            {detectedAt && (
              <>
                {' • '}
                <StoreDateTime value={detectedAt} storeTimeZone={storeTimeZone} />
              </>
            )}
          </p>
          {showProductSelection && (
            <div className="flex gap-6 items-center mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-1 text-sm text-blue-800">
                Boxes for candidate are selected on camera image{' '}
                <span className="inline-block w-4 h-4 rounded border border-gray-400 bg-blue-500"></span>{' '}
                and highlighted on POG{' '}
                <span className="inline-block w-4 h-4 rounded border border-gray-400 bg-yellow-400"></span>
              </div>

              <div className="flex gap-2">
                <Tooltip title="Create match" arrow>
                  <button
                    onClick={onAcceptSelection}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-colors"
                  >
                    Accept
                  </button>
                </Tooltip>
                <Tooltip title="Clear selection" arrow>
                  <button
                    onClick={onRejectSelection}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-colors"
                  >
                    Reject
                  </button>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
        {hasInternalAccess && (
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-4">
              {/* Navigation buttons */}
              {showNavigation && onPreviousImage && onNextImage && (
                <div className="flex items-center gap-2">
                  {navigationExtra && <div className="mr-2">{navigationExtra}</div>}
                  <IconButton
                    title="Previous image"
                    size="small"
                    disabled={isLoadingNavigation}
                    onClick={onPreviousImage}
                    sx={{
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      ...(page !== undefined && {
                        visibility: !page || parseInt(page) === 0 ? 'hidden' : 'visible',
                      }),
                    }}
                  >
                    {isLoadingNavigation ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                    ) : (
                      <ArrowBack fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    title="Next image"
                    size="small"
                    disabled={isLoadingNavigation || disableNextImage}
                    onClick={onNextImage}
                    sx={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
                  >
                    {isLoadingNavigation ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                    ) : (
                      <ArrowForward fontSize="small" />
                    )}
                  </IconButton>
                </div>
              )}
              <button
                onClick={onBack}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Back
              </button>
            </div>
            {extraActions}
          </div>
        )}
      </div>
    </div>
  );
};
